import { useEffect, useState } from 'react';
import { accountsEnabled, requestWithAdultRefresh } from './AdultAuthPanel';

export function invitationState(invitation, now = Date.now()) {
  if (invitation.consumed_at) return 'Aceptada';
  if (invitation.revoked_at) return 'Revocada';
  if (Date.parse(invitation.expires_at) <= now) return 'Expirada';
  return 'Disponible';
}

export default function AdultMembersSection({ workspace, resourceId, onPermissionChange, onMembershipEnded }) {
  const base = `${workspace === 'family' ? '/families' : '/classes'}/${resourceId}`;
  const [members, setMembers] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [secret, setSecret] = useState(null);
  const [state, setState] = useState('loading');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (!accountsEnabled) return undefined;
    let active = true;
    setState('loading');
    onPermissionChange?.(resourceId, false);
    setSecret(null);
    async function load() {
      try {
        const result = await requestWithAdultRefresh(`${base}/members`);
        const pending = result.can_manage ? await requestWithAdultRefresh(`${base}/invitations`) : [];
        if (!active) return;
        setMembers(result.members);
        setCanManage(result.can_manage);
        onPermissionChange?.(resourceId, result.can_manage);
        setInvitations(pending);
        setState('ready');
      } catch (_error) {
        if (!active) return;
        setMembers([]);
        setInvitations([]);
        setCanManage(false);
        setState('error');
      }
    }
    load();
    return () => { active = false; };
  }, [base, revision, resourceId, onPermissionChange]);

  if (!accountsEnabled) return null;

  async function mutate(path, body, returnsSecret = false) {
    setBusy(true);
    setMessage('');
    try {
      const result = await requestWithAdultRefresh(path, {
        method: 'POST',
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      if (returnsSecret) {
        setSecret(result);
        try {
          setInvitations(await requestWithAdultRefresh(`${base}/invitations`));
        } catch (_refreshError) {
          setMessage('La invitación se creó correctamente. Conserva el código; no pudimos actualizar la lista.');
        }
      } else {
        setSecret(null);
        setRevision((value) => value + 1);
      }
      return true;
    } catch (error) {
      setMessage('No fue posible completar la acción. Comprueba tus permisos e inténtalo de nuevo.');
      if (error?.status === 401 || error?.status === 404) {
        onPermissionChange?.(resourceId, false);
        setCanManage(false);
        setMembers([]);
        setInvitations([]);
        setState('error');
      }
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function create(event) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const body = workspace === 'family'
      ? { relationship_label: String(form.get('relationship_label')) }
      : { membership_role: String(form.get('membership_role')) };
    await mutate(`${base}/invitations`, body, true);
  }

  async function end(member) {
    if (!window.confirm(`¿Terminar la membresía de ${member.display_name}? Se conservará el historial.`)) return;
    if (await mutate(`${base}/members/${member.member_id}/end`)) onMembershipEnded();
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(secret.code);
      setMessage('Invitación copiada.');
    } catch (_error) {
      setMessage('Selecciona el código y cópialo manualmente.');
    }
  }

  return (
    <section className="tmag-adult-members" aria-label="Miembros adultos">
      <h2>{workspace === 'family' ? 'Adultos que acompañan' : 'Maestros de la clase'}</h2>
      {state === 'loading' && <p>Cargando miembros…</p>}
      {state === 'error' && <><p>No fue posible cargar los miembros.</p><button type="button" onClick={() => setRevision((v) => v + 1)}>REINTENTAR</button></>}
      {state === 'ready' && <>
        {members.length === 0 && <p>No hay miembros adultos activos.</p>}
        <ul>
          {members.map((member) => <li key={member.member_id}>
            <span>{member.display_name} — {member.role === 'CO_TEACHER' ? 'Maestro colaborador' : member.role === 'TEACHER' ? 'Maestro administrador' : member.relationship_label || 'Adulto de la familia'}</span>
            {canManage && <button type="button" disabled={busy} onClick={() => end(member)}>TERMINAR MEMBRESÍA</button>}
          </li>)}
        </ul>
        {!canManage && <p>Solo un maestro administrador puede gestionar los accesos de esta clase.</p>}
        {canManage && <>
          <form onSubmit={create}>
            {workspace === 'family' ? <label>Relación del adulto invitado
              <select name="relationship_label" defaultValue="TUTOR" disabled={busy}>
                <option value="PADRE">Padre</option><option value="MADRE">Madre</option>
                <option value="TUTOR">Tutor</option><option value="OTRO">Otro</option>
              </select>
            </label> : <label>Permiso en la clase
              <select name="membership_role" defaultValue="CO_TEACHER" disabled={busy}>
                <option value="CO_TEACHER">Maestro colaborador</option><option value="TEACHER">Maestro administrador</option>
              </select>
            </label>}
            <p>Válida por 24 horas y para un solo uso. El adulto necesita el rol {workspace === 'family' ? 'GUARDIAN' : 'TEACHER'} activo.</p>
            <button type="submit" disabled={busy}>CREAR INVITACIÓN</button>
          </form>
          {secret && <div>
            <label>Código de invitación<input readOnly value={secret.code} aria-label="Código de invitación" /></label>
            <p>Guárdalo ahora; no podrás recuperarlo después.</p>
            <button type="button" onClick={copy}>COPIAR INVITACIÓN</button>
          </div>}
          <ul aria-label="Invitaciones adultas">
            {invitations.map((invitation) => <li key={invitation.invitation_id}>
              <span>{invitation.membership_role === 'CO_TEACHER' ? 'Maestro colaborador' : invitation.membership_role === 'TEACHER' ? 'Maestro administrador' : 'Familia'} — {invitationState(invitation)}</span>
              {!invitation.consumed_at && !invitation.revoked_at && <>
                <button type="button" disabled={busy} onClick={() => mutate(`${base}/invitations/${invitation.invitation_id}/rotate`, { expires_in_hours: 24 }, true)}>ROTAR</button>
                <button type="button" disabled={busy} onClick={() => mutate(`${base}/invitations/${invitation.invitation_id}/revoke`)}>REVOCAR</button>
              </>}
            </li>)}
          </ul>
        </>}
      </>}
      {message && <p role="status">{message}</p>}
    </section>
  );
}

export function AcceptAdultInvitation({ workspace, onAccepted }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  useEffect(() => { setCode(''); setMessage(''); }, [workspace]);
  if (!accountsEnabled) return null;
  async function accept(event) {
    event.preventDefault();
    setBusy(true);
    setMessage('');
    const submitted = code.trim();
    setCode('');
    try {
      await requestWithAdultRefresh('/adult-invitations/accept', {
        method: 'POST', body: JSON.stringify({ scope: workspace === 'family' ? 'FAMILY' : 'CLASS', code: submitted }),
      });
      setMessage('Invitación aceptada.');
      onAccepted();
    } catch (_error) {
      setMessage('Invitación no disponible. Comprueba el código y el rol de tu cuenta.');
    } finally {
      setBusy(false);
    }
  }
  return <form className="tmag-adult-workspace__create" onSubmit={accept}>
    <label>Aceptar invitación adulta<input value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" maxLength="128" required disabled={busy} /></label>
    <button type="submit" disabled={busy}>ACEPTAR INVITACIÓN</button>
    {message && <p role="status">{message}</p>}
  </form>;
}
